/**
 * What each footer verb does: the route behind it, called the way the case
 * file always called it (`useResolveWorkItemGate`, `usePageActs`, the merge
 * and request-changes routes, the stop route).
 *
 * A verb that writes runs the settle: the sheet body dims for 420 ms while the
 * write lands, then the ticket re-reads and the queue reflows, so the row
 * moves group under the reader's eye. A verb that only moves the reader (Watch
 * live, Next item, View PR) or drafts words for the Ask dock (Request
 * changes, Ask @yagni) never settles.
 *
 * Send back at the plan gate writes on the sheet, not in the dock: the first
 * press opens the note box above the footer, the next sends the note. Reeve
 * edits the plan for it and the gate stays open on the new version (spec
 * 2026-11-02). Approve names the version on the page; the answers are already
 * in it, saved one by one as they were given (`useTicket`), so it carries
 * none. A plan the route says moved, or does not yet hold every answer, is
 * read again and the route's sentence said.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { JobActChoiceId, WorkItem, WorkItemGate } from "@yagni/shared";
import {
  isLiveRun,
  jobActCappedCopy,
  readGateAsk,
  readJobGatePayload,
  resumesApprovedPlan,
} from "@yagni/shared";
import {
  GateResolveError,
  useResolveWorkItemGate,
  type ResolveGateInput,
} from "../tracker/useWorkItemGate.js";
import { usePageActs } from "../work/usePageActs.js";
import { askDockStore } from "../shell/askDockStore.js";
import { announceWorkItemChange } from "../work/useWorkItemEntityChange.js";
import { REPO_REMOVED_CODE } from "../work/repoPick.js";
import { captureUiError } from "../sentry.js";
import type { MergeFacts } from "../work/MergeFooter.js";
import type { ReviewFindingsState } from "../work/ReviewFindings.js";
import type { SheetVerbId } from "./sheetFooter.js";
import { rowKeyOf } from "./queueGroups.js";

/** A resolve body without its ids, one per action (distributed over the union). */
type ResolveBody = ResolveGateInput extends infer T
  ? T extends unknown
    ? Omit<T, "workItemId" | "gateId">
    : never
  : never;

/** The settle, in milliseconds: `--settle-duration` in tokens.css. */
export const SETTLE_MS = 420;

export interface SheetActsInput {
  item: WorkItem;
  gate: WorkItemGate | null;
  runs: ReadonlyArray<{ id: string; status: string }>;
  /** The plan version on the page: what Approve approves (spec 2026-11-02). */
  planVersionId?: string | null;
  /** The plan's design evidence, as the approval guards it. */
  expectedPrototypeReferencesHash?: string | null;
  /** The written answer to a needs_input ask. */
  answer: string;
  /** A note the footer collected for a verb that needs words. */
  note: string;
  /** The verb whose note box is open on the sheet, when one is (`onNeedsNote` opened it). */
  noteFor?: SheetVerbId | null;
  merge: MergeFacts | null;
  review: ReviewFindingsState | null;
  /** The write landed and settled: re-read the ticket. */
  onSettled: () => void;
  /** Watch live: the sheet takes the page. */
  onWide: () => void;
  /** Next item: the host opens the next ticket that needs the reader, or closes. */
  onNext: () => void;
  /** A discarded ticket: open the attempt that replaced it. */
  onOpenSuccessor?: () => void;
  /** Start over: the sheet asks for the reason first (`StartOverDialog`), which does the write. */
  onStartOver?: () => void;
  /** Steer: the sheet asks for its words (`AddNoteDialog`), which does the write. */
  onAddNote?: () => void;
  /** Read the open gate again, after a read that failed. */
  onRetryGate: () => void;
  /** A verb wants a note written in the sheet first. */
  onNeedsNote: (verb: SheetVerbId) => void;
  /** Open a page inside the app (the ask's link). */
  onOpenPath?: (path: string) => void;
  /** Take the Ask dock back for this ticket before a verb drafts in it. */
  claimDock?: () => void;
  /**
   * The ticket names no repository and its Team has several: Start offers the
   * one-field pick before planning (the spec's choose_repo), not a plan with
   * no repository.
   */
  needsRepoChoice?: boolean;
}

/** The route's own sentence for a refusal, else a plain one. */
function refusalSentence(payload: { error?: unknown; message?: unknown } | null): string {
  if (typeof payload?.message === "string" && payload.message.trim()) return payload.message;
  if (typeof payload?.error === "string" && payload.error.trim()) return payload.error;
  return "That did not go through. Try again.";
}

/** What an accept with `startPlanning` says about planning: the gate resolve and the accept route both carry it as `planning`. */
function planningOf(body: unknown): { status?: unknown; code?: unknown; reason?: string } | null {
  if (!body || typeof body !== "object") return null;
  const planning = (body as { planning?: unknown }).planning;
  if (!planning || typeof planning !== "object") return null;
  const { status, code, reason } = planning as {
    status?: unknown;
    code?: unknown;
    reason?: unknown;
  };
  return { status, code, ...(typeof reason === "string" && reason.trim() ? { reason } : {}) };
}

const CHOOSE_REPO_SENTENCE = "Choose the repository this ticket builds in, then start planning.";

/** True when an accept started Bailey's reading (`{ scopeNow: { status: "reading" } }`) instead of planning. */
function scopeNowOf(body: unknown): boolean {
  if (!body || typeof body !== "object") return false;
  const scoped = (body as { scopeNow?: unknown }).scopeNow;
  return (
    Boolean(scoped && typeof scoped === "object") &&
    (scoped as { status?: unknown }).status === "reading"
  );
}

/** Plan without Bailey's check, when today's readings turned out not to be spent. */
export const SCOPE_NOW_INSTEAD =
  "Today's readings were not used up after all, so Bailey is checking this ticket against the code first. Reeve plans it once the spec is posted.";

async function post(url: string, body?: unknown): Promise<unknown> {
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const payload = (await res.json().catch(() => null)) as {
    error?: unknown;
    message?: unknown;
  } | null;
  if (!res.ok) {
    throw new Error(refusalSentence(payload));
  }
  return payload;
}

/** What the footer says when Send back is pressed with nothing to send. */
export const SEND_BACK_NEEDS_WORDS =
  "Write a note first, so Reeve knows what to change in the plan.";

/**
 * The approval's refusals that say the plan on the page is not the one to
 * approve yet (`planApprovalRefusal` on the server): the sheet reads the gate
 * again and says the route's sentence. Expected, so never reported.
 */
export const PLAN_APPROVAL_REFUSALS: ReadonlySet<string> = new Set([
  "plan_version_stale",
  "plan_edit_in_progress",
  "plan_answer_failed",
  "plan_changes_pending",
  "plan_questions_open",
]);

/** The words a chat-backed verb puts in the Ask bar. */
export function dockDraftFor(verb: SheetVerbId, item: WorkItem): string | null {
  const key = rowKeyOf(item);
  switch (verb) {
    case "ask_about_plan":
      // A question, worded as one, so the plan-review chat answers it and
      // never reads it as a send-back (ENG-2966).
      return `On ${key}, a question about the plan: `;
    case "request_changes":
      return `On ${key}, request changes: `;
    case "ask_yagni":
      return `On ${key}, what does this need from me? `;
    default:
      return null;
  }
}

export function useSheetActs(input: SheetActsInput) {
  const { resolveGate } = useResolveWorkItemGate();
  const [pending, setPending] = useState<SheetVerbId | null>(null);
  const [settling, setSettling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The route's stable code for the refusal, when it sent one (`choose_repo`
  // and `needs_repo` are the ones the sheet can do something about).
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(input);
  latest.current = input;
  useEffect(
    () => () => {
      if (timer.current != null) clearTimeout(timer.current);
    },
    [],
  );
  const acts = usePageActs(input.item.id);
  // A new ticket in the sheet starts clean.
  useEffect(() => {
    setError(null);
    setErrorCode(null);
    setPending(null);
    setSettling(false);
  }, [input.item.id]);

  const settle = useCallback(() => {
    setSettling(true);
    timer.current = setTimeout(() => {
      setSettling(false);
      window.dispatchEvent(new CustomEvent("tickets.invalidate"));
      announceWorkItemChange(latest.current.item.id);
      latest.current.onSettled();
    }, SETTLE_MS);
  }, []);

  const run = useCallback(
    async (verb: SheetVerbId, opts: { repoChosen?: boolean } = {}) => {
      const { item, gate, runs, answer, note, merge, review } = latest.current;
      const draft = dockDraftFor(verb, item);
      if (draft) {
        latest.current.claimDock?.();
        askDockStore.prefill(draft);
        return;
      }
      if (verb === "watch_live") {
        latest.current.onWide();
        return;
      }
      if (verb === "retry_gate") {
        latest.current.onRetryGate();
        return;
      }
      if (verb === "next_item") {
        latest.current.onNext();
        return;
      }
      if (verb === "open_successor") {
        latest.current.onOpenSuccessor?.();
        return;
      }
      if (verb === "start_over") {
        latest.current.onStartOver?.();
        return;
      }
      if (verb === "add_note") {
        latest.current.onAddNote?.();
        return;
      }
      if (verb === "open_link") {
        // The ask's link is an app path, checked when it was read (`readGateAsk`).
        const href = gate ? readGateAsk(gate.payload)?.link?.href : null;
        if (href) latest.current.onOpenPath?.(href);
        return;
      }
      if (verb === "open_pr") {
        const url = merge?.pullRequestUrl ?? item.prUrl;
        if (url) window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      if (verb === "request_result_changes" && !note.trim()) {
        latest.current.onNeedsNote(verb);
        return;
      }
      if (verb === "send_back") {
        // The first Send back opens the note box, so the reader can say what
        // the plan never asked about; the next one sends the note. With no
        // note there is nothing to edit the plan for, so the footer says so
        // and the box stays open.
        if (latest.current.noteFor !== "send_back" && !note.trim()) {
          latest.current.onNeedsNote(verb);
          return;
        }
        if (!note.trim()) {
          setError(SEND_BACK_NEEDS_WORDS);
          setErrorCode(null);
          return;
        }
      }

      const ids = gate ? { workItemId: item.id, gateId: gate.id } : null;
      const resolve = async (body: ResolveBody) => {
        if (!ids) throw new Error("This ask has already been answered.");
        return await resolveGate({ ...ids, ...body } as ResolveGateInput);
      };
      const job = gate ? readJobGatePayload(gate.payload) : null;
      const capped =
        job?.act === "push_fix" && job.cappedByPerItemCap
          ? jobActCappedCopy(job.act, job.job?.agentName)
          : null;
      const workItemUrl = `/api/work-items/${encodeURIComponent(item.id)}`;

      setPending(verb);
      setError(null);
      setErrorCode(null);
      try {
        switch (verb) {
          case "start_plan": {
            const accepted = gate
              ? await resolve({ action: "accept_proposal", scopePatch: null, startPlanning: true })
              : await post(`${workItemUrl}/accept`, { phase: "accepted", startPlanning: true });
            // Accepted, but the Team has several repositories and the ticket
            // names none: planning waits for a pick. The sheet asks for it in
            // place (the repository choice), and picking one starts planning.
            const planning = planningOf(accepted);
            if (scopeNowOf(accepted)) {
              // A queued tracker ticket: the server started Bailey's reading
              // instead of the plan (`trackerStartPlan`), and the sheet says so.
              setError(SCOPE_NOW_INSTEAD);
            } else if (planning?.status === "not_started" && planning.code === "choose_repo") {
              setError(planning.reason ?? CHOOSE_REPO_SENTENCE);
              setErrorCode("choose_repo");
            } else if (planning?.status === "not_started" && planning.reason) {
              // Any other hold (a full concurrency slot, a paused rollout):
              // the route's own sentence, so the ticket never sits unexplained.
              setError(planning.reason);
            }
            break;
          }
          case "scope_now":
            // Bailey reads this queued tracker ticket next; nothing is accepted.
            await post(`${workItemUrl}/scope-now`);
            break;
          case "keep_proposal":
            if (gate) await resolve({ action: "decline_proposal" });
            else await post(`${workItemUrl}/dismiss`);
            break;
          case "approve_plan": {
            const planId =
              gate?.planId ??
              (typeof gate?.payload?.planId === "string" ? gate.payload.planId : null);
            if (!planId) throw new Error("This plan has no revision to approve yet.");
            const planVersionId = latest.current.planVersionId;
            await resolve({
              action: "approve_execution_plan",
              planId,
              ...(planVersionId ? { planVersionId } : {}),
              ...(latest.current.expectedPrototypeReferencesHash
                ? {
                    expectedPrototypeReferencesHash: latest.current.expectedPrototypeReferencesHash,
                  }
                : {}),
            });
            break;
          }
          case "send_back":
            // The note is queued for the plan: Reeve edits the plan for it,
            // and the gate stays open on the version that comes back.
            await resolve({ action: "request_plan_changes", feedback: note.trim() });
            break;
          case "send_answer":
            await resolve({
              action: "answer_input",
              response: { text: answer.trim() },
              resume: true,
            });
            break;
          case "run_again_gate":
            await resolve({
              action: "answer_input",
              response: { text: answer.trim() },
              resume: true,
            });
            break;
          case "continue_anyway":
            await resolve({ action: "continue_anyway" });
            break;
          case "patch_plan":
            await resolve({ action: "patch_plan" });
            break;
          case "reoffer_plan":
            // A stop that keeps the approved plan runs it again the way Run
            // again does; a plan stop puts the plan back at its gate.
            if (gate && resumesApprovedPlan(gate)) {
              await resolve({
                action: "answer_input",
                response: { text: answer.trim() },
                resume: true,
              });
            } else {
              await acts.reofferPlan({ gateId: gate?.id ?? null });
            }
            break;
          case "send_back_to_plan":
            await resolve({
              action: "stop_to_planning",
              ...(answer.trim() ? { note: answer.trim() } : {}),
            });
            break;
          case "dismiss_ask":
            await resolve({ action: "dismiss_input" });
            break;
          case "keep_for_human":
            await post(`${workItemUrl}/dismiss`);
            break;
          case "stop_run": {
            const live = runs.find(isLiveRun) ?? runs[0];
            const runId = live?.id ?? item.activeRun?.id ?? item.currentRunId;
            if (!runId) throw new Error("Nothing is running to stop.");
            await post(`/api/runs/${encodeURIComponent(runId)}/take-over`);
            break;
          }
          case "hand_back":
            await post(`${workItemUrl}/hand-back`);
            break;
          case "run_again": {
            const latestRun = runs[0];
            if (!latestRun) throw new Error("There is no attempt to run again.");
            await acts.runAgain(latestRun.id);
            break;
          }
          case "start_planning": {
            if (latest.current.needsRepoChoice && !opts.repoChosen) {
              setError(CHOOSE_REPO_SENTENCE);
              setErrorCode("choose_repo");
              return;
            }
            const result = (await post(`${workItemUrl}/start-planning`, {})) as {
              held?: unknown;
            } | null;
            if (result?.held === true)
              setError("Waiting for a free slot. Planning starts on its own.");
            break;
          }
          case "one_more_round":
            await resolve({ action: "perform_job_act", choice: "continue" as JobActChoiceId });
            break;
          case "stop_round":
            if (capped?.declineChoice)
              await resolve({ action: "perform_job_act", choice: capped.declineChoice });
            else await resolve({ action: "decline_job_act" });
            break;
          case "act":
            // A stopped round's retry carries the person's note, when they left one.
            await resolve({
              action: "perform_job_act",
              ...(note.trim() ? { feedback: note.trim() } : {}),
            });
            break;
          case "take_over":
            // No note: the sheet drops the note box when Take it over displaces
            // the retry (`stoppedRoundFooter`); a note belongs to a retry.
            await resolve({ action: "perform_job_act", choice: "take_over" as JobActChoiceId });
            break;
          case "decline_act":
            await resolve({
              action: "decline_job_act",
              ...(note.trim() ? { feedback: note.trim() } : {}),
            });
            break;
          case "merge":
            await post(`${workItemUrl}/merge`);
            break;
          case "fix_conflicts":
            await post(`${workItemUrl}/fix-conflicts`);
            break;
          case "confirm_review": {
            const findings = review?.resolutions ?? [];
            await resolve({
              action: "accept_result",
              ...(findings.length > 0 ? { findings } : {}),
            });
            break;
          }
          case "confirm_held_review": {
            const held = merge?.heldReview;
            if (!held) throw new Error("The review is no longer held.");
            await resolveGate({
              workItemId: held.caseId,
              gateId: held.gateId,
              action: "accept_result",
            });
            break;
          }
          case "mark_done":
            await resolve({ action: "accept_result" });
            break;
          case "request_result_changes":
            await resolve({ action: "request_result_changes", feedback: note.trim() });
            break;
          case "commission": {
            const projectId =
              typeof gate?.payload?.projectId === "string" ? gate.payload.projectId : null;
            if (!projectId || !gate) throw new Error("This ask names no project.");
            await post(`/api/projects/${encodeURIComponent(projectId)}/commission`, {
              gateId: gate.id,
            });
            break;
          }
          case "push_milestone": {
            const projectId =
              typeof gate?.payload?.projectId === "string" ? gate.payload.projectId : null;
            const milestoneId =
              typeof gate?.payload?.milestoneId === "string" ? gate.payload.milestoneId : null;
            if (!projectId || !milestoneId || !gate)
              throw new Error("This ask names no milestone.");
            await post(
              `/api/projects/${encodeURIComponent(projectId)}/milestones/${encodeURIComponent(milestoneId)}/push`,
              { gateId: gate.id },
            );
            break;
          }
          default:
            return;
        }
        settle();
      } catch (err) {
        const body =
          err instanceof GateResolveError ? (err.body as { code?: unknown } | null) : null;
        const code = typeof body?.code === "string" ? body.code : null;
        // The plan moved, or does not hold every answer yet: expected, so it
        // is not reported, and the gate is read again to show the plan as it is.
        // A repository removed from the workspace is expected too: the ticket
        // is read again, and its removed repository holds the verbs.
        const planRefused = code != null && PLAN_APPROVAL_REFUSALS.has(code);
        const repoRemoved = code === REPO_REMOVED_CODE;
        if (!planRefused && !repoRemoved) {
          captureUiError(err, { area: "Work", operation: `sheet_${verb}`, workItemId: item.id });
        }
        setError(
          err instanceof Error && err.message ? err.message : "That did not go through. Try again.",
        );
        setErrorCode(code);
        if (planRefused || repoRemoved) latest.current.onRetryGate();
        if (repoRemoved) announceWorkItemChange(item.id);
      } finally {
        setPending(null);
      }
    },
    [acts, resolveGate, settle],
  );

  return {
    run,
    pending,
    settling,
    error,
    errorCode,
    clearError: () => {
      setError(null);
      setErrorCode(null);
    },
    /** A hold the sheet learned outside a verb (a repository pick's planning outcome). */
    showHold: (sentence: string) => {
      setError(sentence);
      setErrorCode(null);
    },
  };
}
